import { createInfiniteQuery, createMutation } from 'react-query-kit';

import { createData, fetchData, patchData, removeData } from 'services/http/http';
import { queryClient } from 'services/http/queryClient';
import portalSchema from 'support/types/schema-portal';

export type ProductMasterdataResponseDTO =
  portalSchema.paths['/v1/partners/current/masterdata/products']['get']['responses']['200']['content']['application/json'];

export type PartnerProductDTO = ProductMasterdataResponseDTO['data'][number];

export type ProductTimelineEventDTO = portalSchema.components['schemas']['ProductTimelineEvent'];

export const useInfiniteProductMasterdata = createInfiniteQuery<
  ProductMasterdataResponseDTO,
  { search?: string },
  Error
>({
  primaryKey: '/masterdata/products',
  queryFn: async ({ queryKey: [, variables], pageParam }) => {
    const params = new URLSearchParams();
    if (pageParam) {
      params.set('cursor', String(pageParam));
    }
    if (variables?.search) {
      params.set('filter[search]', variables.search);
    }
    const qs = params.toString();
    const { data } = await fetchData<ProductMasterdataResponseDTO>(
      `/v1/partners/current/masterdata/products${qs ? `?${qs}` : ''}`,
    );
    return data;
  },
  getNextPageParam: (lastPage) => lastPage.meta.next_cursor,
  getPreviousPageParam: (_, allPages) => allPages.at(-1)?.meta.prev_cursor,
});

export type UpdateProductMasterdataPayload =
  portalSchema.paths['/v1/partners/current/masterdata/products']['patch']['requestBody']['content']['application/json'];

export type PartnerProductUpdateDTO = UpdateProductMasterdataPayload['products'][number];

type UpdateProductMasterdataResponseDTO =
  portalSchema.paths['/v1/partners/current/masterdata/products']['patch']['responses']['200']['content']['application/json'];

export const useUpdateProductMasterdata = createMutation<
  Array<PartnerProductDTO>,
  UpdateProductMasterdataPayload,
  Error
>({
  mutationFn: async (payload) => {
    const { data } = await patchData<UpdateProductMasterdataResponseDTO>(
      '/v1/partners/current/masterdata/products',
      payload,
    );
    return data.data;
  },
});

/**
 * Refetches the product list. A save is a PATCH → POST → DELETE sequence, so none of the three
 * mutations invalidate on their own: refetching between steps would change the loaded rows (the
 * edit's baseline) while the edit is still in progress. `useProductMasterdataSave` calls this once
 * after the last step and awaits it, so the page's mode-sync effect sees fresh rows the moment it
 * flips back to view — not a stale set from before the save, which would otherwise flash briefly
 * while the refetch was in flight.
 */
export const invalidateProductMasterdata = () =>
  queryClient.invalidateQueries({ queryKey: useInfiniteProductMasterdata.getKey() });

/**
 * Flags the cached list as stale without refetching it. A save calls this after each request that
 * went through, so that if a later request fails and the user leaves the page instead of retrying,
 * the next visit refetches (`staleTime` is 5 minutes, and nothing else would) rather than showing
 * products the server has already deleted. It must not refetch: the list's rows are the edit's
 * baseline, and changing them mid-edit is exactly what `invalidateProductMasterdata` is kept for
 * the very end to avoid.
 */
export const markProductMasterdataStale = () =>
  queryClient.invalidateQueries({ queryKey: useInfiniteProductMasterdata.getKey(), refetchType: 'none' });

export type CreateProductMasterdataPayload =
  portalSchema.paths['/v1/partners/current/masterdata/products']['post']['requestBody']['content']['application/json'];

export type PartnerProductCreateDTO = CreateProductMasterdataPayload['products'][number];

type CreateProductMasterdataResponseDTO =
  portalSchema.paths['/v1/partners/current/masterdata/products']['post']['responses']['201']['content']['application/json'];

export const useCreateProductMasterdata = createMutation<
  Array<PartnerProductDTO>,
  CreateProductMasterdataPayload,
  Error
>({
  mutationFn: async (payload) => {
    const { data } = await createData<CreateProductMasterdataResponseDTO>(
      '/v1/partners/current/masterdata/products',
      payload,
    );
    return data.data;
  },
});

export type DeleteProductMasterdataPayload =
  portalSchema.paths['/v1/partners/current/masterdata/products']['delete']['requestBody']['content']['application/json'];

export const useDeleteProductMasterdata = createMutation<void, DeleteProductMasterdataPayload, Error>({
  mutationFn: async (payload) => {
    await removeData<void>('/v1/partners/current/masterdata/products', payload);
  },
});
